import { useEffect, useState } from "react";
import { Loader2, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Pending", "Requested", "Paid", "Waived", "Cancelled"];
const COLOR = (s) => ({ Pending: "bg-amber-100 text-amber-700", Requested: "bg-blue-100 text-blue-700", Paid: "bg-emerald-100 text-emerald-700", Waived: "bg-slate-100 text-slate-500", Cancelled: "bg-red-100 text-red-700" }[s]);
const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(n || 0)}`;
const waLink = (n) => { if (!n) return null; const d = n.replace(/\D/g, ""); return `https://wa.me/${d.length === 10 ? "91" + d : d}`; };

export default function AdminServiceCharges() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [payFor, setPayFor] = useState(null);

  const load = () => {
    setLoading(true);
    const params = status !== "all" ? { status } : {};
    api.get("/admin/service-charges", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  const quickSet = async (sc, newStatus) => {
    try { await api.patch(`/admin/service-charges/${sc.id}`, { payment_status: newStatus, payment_method: sc.payment_method || "", transaction_ref: sc.transaction_ref || "", notes: sc.notes || "" }); toast.success(`Marked ${newStatus}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const waSeller = (sc) => {
    const url = waLink(sc.seller_whatsapp); if (!url) return null;
    const msg = `*Mashiniya Service Charge ${sc.sc_code}*\nMachine: ${sc.machine_name}\nFixed post-sale service charge: ${inr(sc.amount)}\nKindly complete the payment. Thank you for using Mashiniya.`;
    return `${url}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Service Charges</h1>
        <div className="w-44">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="sc-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 space-y-3">
          {items.length === 0 && <p className="py-12 text-center text-slate-500">No service charges yet. They are generated automatically when a deal is completed.</p>}
          {items.map((sc) => (
            <div key={sc.id} data-testid={`sc-row-${sc.sc_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-400">{sc.sc_code}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${COLOR(sc.payment_status)}`}>{sc.payment_status}</span>
                  </div>
                  <p className="mt-0.5 font-display text-xl font-extrabold text-slate-900">{inr(sc.amount)}</p>
                  <p className="text-sm text-slate-500">{sc.machine_name} • Seller: {sc.seller_name} • Buyer: {sc.buyer_name}</p>
                  {sc.due_date && <p className="text-xs text-slate-400">Due: {String(sc.due_date).slice(0, 10)}{sc.payment_date ? ` • Paid: ${String(sc.payment_date).slice(0, 10)}` : ""}</p>}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {waSeller(sc) && <a href={waSeller(sc)} target="_blank" rel="noreferrer" title="WhatsApp Seller" className="rounded-lg bg-whatsapp p-2 text-white"><MessageCircle size={15} /></a>}
                  {sc.payment_status !== "Paid" && sc.payment_status !== "Cancelled" && (
                    <>
                      {sc.payment_status === "Pending" && <button data-testid={`sc-request-${sc.sc_code}`} onClick={() => quickSet(sc, "Requested")} className="rounded-full border border-blue-300 px-3 py-1.5 text-xs font-bold text-blue-700">Request</button>}
                      <button data-testid={`sc-paid-${sc.sc_code}`} onClick={() => setPayFor(sc)} className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white">Mark Paid</button>
                      <button data-testid={`sc-waive-${sc.sc_code}`} onClick={() => quickSet(sc, "Waived")} className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600">Waive</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {payFor && <PaidModal sc={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }} />}
    </div>
  );
}

function PaidModal({ sc, onClose, onSaved }) {
  const [f, setF] = useState({ payment_method: "UPI", transaction_ref: "", payment_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/service-charges/${sc.id}`, { payment_status: "Paid", ...f }); toast.success("Marked paid"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sc-paid-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Mark Paid — {sc.sc_code} ({inr(sc.amount)})</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Payment method</Label>
            <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["UPI", "Bank Transfer", "Cash", "Cheque", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Transaction reference</Label><Input data-testid="sc-txn-ref" value={f.transaction_ref} onChange={(e) => setF({ ...f, transaction_ref: e.target.value })} /></div>
          <div><Label>Payment date</Label><Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} /></div>
          <button data-testid="sc-paid-confirm" onClick={save} disabled={saving} className="w-full rounded-full bg-emerald-600 py-2.5 font-bold text-white disabled:opacity-60">{saving ? "Saving…" : "Confirm Paid"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
